import { Clipboard } from "@capacitor/clipboard";
import { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import Button from "@components/buttons/Button";
import styles from "@components/expertSection/ExpertSection.module.css";
import Tag from "@components/labels/Tag";
import Nbsp from "@components/Nbsp";
import AlertPopover from "@components/popovers/AlertPopover";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import { ContactMethodEnum, GetJourneyWithExpertAndConsultsQuery } from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";
import { consultEndsInFuture, consultStartsInFuture } from "@utils/timeUtils";

type Props = {
  consult: GetJourneyWithExpertAndConsultsQuery["journey"]["consults"][0];
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
};

const ExpertSessionJoinInfo = ({ consult, journey }: Props) => {
  const myExpert = useMyExpert();

  const handleCopyPhoneNumber = useCallback(() => {
    if (consult.phoneNumber?.number) {
      Clipboard.write({ string: consult.phoneNumber.number });
    }
  }, [consult.phoneNumber]);

  const hasPublishedReport = useMemo((): boolean => {
    if (journey.initialConsultReport.consult?.id === consult.id) {
      return journey.initialConsultReport.published;
    } else {
      return consult.consultReport?.published ?? false;
    }
  }, [
    consult.consultReport?.published,
    consult.id,
    journey.initialConsultReport.consult?.id,
    journey.initialConsultReport.published
  ]);

  const joinData = (
    <div>
      {consult.joinUrl && (
        <Button href={consult.joinUrl} rel="noopener noreferrer" target="_blank">
          <IconBase icon={consult.contactMethod === ContactMethodEnum.PHONE ? "phone" : "video"} slot="start" />
          <FormattedMessage id="pages.expertSection.join" />
        </Button>
      )}
      {consult.contactMethod === ContactMethodEnum.PHONE && (
        <AlertPopover textKey="dictionary.copied">
          <div className={styles.contactMethodNumber} onClick={handleCopyPhoneNumber}>
            <BodyCopy color="primary">{consult.phoneNumber?.formattedNumber}</BodyCopy>
          </div>
        </AlertPopover>
      )}
    </div>
  );

  const finalizedStatus = (
    <>
      {hasPublishedReport ? (
        <Tag color="success">
          <FormattedMessage id="models.consult.finalized" />
        </Tag>
      ) : (
        <InternalLink
          linkEventCreateVariables={{ journeyId: consult.journey.id }}
          stylized={false}
          to={journeyRoute({
            journeyId: consult.journey.id,
            linkAfterJourney: `/care-profile?docSection=sessions`,
            myExpert
          })}
        >
          <Tag color="danger">
            <FormattedMessage id="models.consult.notFinalized" />
          </Tag>
        </InternalLink>
      )}
    </>
  );

  if (consult.canceled) {
    return (
      <div>
        <BodyCopy color="danger-500">
          <FormattedMessage id="pages.expertSection.canceled" />
          {consult.cancelationReason && (
            <>
              <Nbsp />
              <FormattedMessage id="dictionary.punctuation.dash" />
              <Nbsp /> <FormattedMessage id={`models.consult.${consult.cancelationReason}`} />
            </>
          )}
        </BodyCopy>
      </div>
    );
  }

  if (consultStartsInFuture(consult)) {
    return (
      <div className={styles.consultItems}>
        <BodyCopy color="success-500">
          <FormattedMessage id="pages.expertSection.scheduled" />
        </BodyCopy>
        {joinData}
      </div>
    );
  }

  if (consultEndsInFuture(consult)) {
    return (
      <div className={styles.consultItems}>
        <BodyCopy color="success-500">
          <FormattedMessage id="pages.expertSection.inProgress" />
        </BodyCopy>
        {joinData}
        {finalizedStatus}
      </div>
    );
  }

  return (
    <div className={styles.consultItems}>
      <BodyCopy color="primary">
        <FormattedMessage id={`models.duration.${consult.durationType}`} />
      </BodyCopy>
      {finalizedStatus}
    </div>
  );
};

export default ExpertSessionJoinInfo;
